import { Eye, User, Ban, CheckCircle, Building, List } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { useState, useMemo, useEffect } from "react";
import { EMPLOYEE_PERSONAL_INFO, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION } from "../../constants/api";
import { checkUserPermissionFunc, numericRecordId } from "../../components/common/CommonFunctions";
import { hasAnyEmployeeProfileView } from "../../utils/employeeProfileAccess";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import { useIntegrationMappings, IntegratedBadge } from "../integration/integrationBadge";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";
import { BRANCH_FILTER_CHANGED_EVENT } from "../../utils/branchSession";
import OccupancyView from "./OccupancyView";

const USER_ROLE_FILTER_OPTIONS = [
    { value: "Org Admin", label: "Org Admin" },
    { value: "Branch Admin", label: "Branch Admin" },
    { value: "Department Admin", label: "Department Admin" },
    { value: "System Admin", label: "System Admin" },
    { value: "Employee", label: "Employee" },
];

const EmployeeList = () => {
    const { fetch, update } = useApiMethods();
    const integrationMap = useIntegrationMappings();
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee_name");
    const [employeeData, setEmployeeData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [actionType, setActionType] = useState("");
    const [viewMode, setViewMode] = useState("list");
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);

    const { edit: editPermission, add: addPermission, active: isActive, permission: viewPermission } = checkUserPermissionFunc("employee", "employees", "SubMenu");
    const canOpenEmployeeFile = !!editPermission || hasAnyEmployeeProfileView() || !!viewPermission;
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setViewMode(location.pathname.includes("/occupancy") ? "occupancy" : "list");
    }, [location.pathname]);

    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [deptRes, desigRes, empRes] = await Promise.all([
                    fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" }),
                    fetch(EMPLOYEE_DESIGNATION, { drop_down: "True" }),
                    fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
                ]);
                const departments = Array.isArray(deptRes?.data) ? deptRes.data : (deptRes?.data?.result || []);
                const designations = Array.isArray(desigRes?.data) ? desigRes.data : (desigRes?.data?.result || []);
                const employees = Array.isArray(empRes?.data) ? empRes.data : (empRes?.data?.result || []);
                setDepartmentOptions(
                    (departments || []).map((row) => ({
                        value: String(row.id),
                        label: row.department_name || row.name,
                    })).filter((row) => row.label)
                );
                setDesignationOptions(
                    (designations || []).map((row) => ({
                        value: String(row.id),
                        label: row.designation_name || row.name,
                    })).filter((row) => row.label)
                );
                setManagerOptions(
                    (employees || []).map((row) => ({
                        value: String(row.id),
                        label: `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.employee_name || row.display_name,
                    })).filter((row) => row.label)
                );
            } catch (error) {
                console.error("Failed to load employee list filters", error);
            }
        };
        loadFilterOptions();
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, loadFilterOptions);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, loadFilterOptions);
    }, [fetch]);

    const switchView = (mode) => {
        setViewMode(mode);
        navigate(mode === "occupancy" ? "/employee/occupancy" : "/employee");
    };

    const employeeColumns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row.original || row;

                const rawProfile = record?.profile;
                const profileUrl = Array.isArray(rawProfile)
                    ? (rawProfile[0] || "")
                    : (typeof rawProfile === "string" ? rawProfile : "");
                const empDetails = {
                    ...record,
                    profile: profileUrl,
                    designation_id: record?.designation_id?.length > 0
                        ? { ...record.designation_id[0] }
                        : { id: 0, designation_name: "" },
                    id: record?.employee_id || record?.id || "",
                    employee_id: record?.employee_id || record?.id || "",
                };

                const mapping = integrationMap.get(String(record.id));
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        <IntegratedBadge mapping={mapping} withLabel={false} />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        {
            accessor: "department",
            Header: "Department",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: departmentOptions,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                return record?.department_id?.[0]?.department_name || "-";
            },
        },
        {
            accessor: "designation",
            Header: "Designation",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: designationOptions,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                return record?.designation_id?.[0]?.designation_name || "-";
            },
        },
        {
            accessor: "user_role",
            Header: "Employee Type",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: USER_ROLE_FILTER_OPTIONS,
            width: "110px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const role = record?.user_role || "Employee";
                const tone =
                    role.includes("Org Admin")
                        ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                        : role.includes("Branch Admin")
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            : role.includes("Department Admin")
                                ? "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                                : role.includes("System Admin")
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
                return (
                    <span className={cn("px-2 py-1 text-xs font-medium rounded-full", tone)}>
                        {role}
                    </span>
                );
            },
        },
        {
            accessor: "reporting_manager_id",
            Header: "Reporting Manager",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: managerOptions,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;

                if (!record?.reporting_manager_id?.length) {
                    return "-";
                }

                const manager = record.reporting_manager_id[0];
                const firstName = manager?.first_name || "";
                const lastName = manager?.last_name || "";

                return `${firstName} ${lastName}`.trim() || "-";
            },
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => {
                const active = Boolean(value);

                return (
                    <span
                        className={cn(
                            "px-2 py-1 text-xs font-medium rounded-full capitalize",
                            active
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        )}
                    >
                        {active ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            accessor: "date_of_joining",
            Header: "Date of Joining",
            sortable: true,
            filterable: true,
            filterType: "date",
            width: "150px",
            Cell: ({ value }) => {
                if (!value) return "-";

                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            },
        },
        ...(canOpenEmployeeFile ? [
            {
                accessor: "actions",
                Header: "Actions",
                fixed: true,
                align: "right",
                Cell: ({ row }) => {
                    const record = row.original || row;

                    const handleView = () => {
                        navigate("/employee/viewDetails", {
                            state: {
                                row: record,
                                employeeID: numericRecordId(record.id),
                                isActive: isActive,
                            },
                        });
                    };

                    return (
                        <div className="flex gap-1 justify-end">
                            <Button
                                variant="ghost"
                                onClick={handleView}
                                className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                title="View Employee Details"
                            >
                                <Eye className="text-orange-500 h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            },
        ] : []),
    ]), [canOpenEmployeeFile, departmentOptions, designationOptions, editPermission, insertBranchColumn, integrationMap, isActive, managerOptions, navigate, showBranchColumn, TempTransferBadge]);

    const handleAddEmployee = () => {
        navigate("/employee/add");
    };

    const handleConfirmToggle = async () => {
        if (!selectedRow) return;

        setLoading(true);
        try {
            const employeeId = selectedRow.id;
            const newStatus = actionType === "activate";

            const payload = {
                employee_id: employeeId,
                is_active: newStatus,
            };

            await update(EMPLOYEE_PERSONAL_INFO, payload);

            setEmployeeData((prevData) =>
                prevData.map((employee) =>
                    employee.employee_id === employeeId || employee.id === employeeId
                        ? { ...employee, is_active: newStatus }
                        : employee
                )
            );

            setServerTrigger(true);
        } catch (error) {
            console.error(`Error ${actionType}ing employee:`, error);
        } finally {
            setLoading(false);
            setIsConfirmOpen(false);
            setSelectedRow(null);
            setActionType("");
            setServerTrigger(false);
        }
    };

    const getModalConfig = () => {
        const isActivating = actionType === "activate";
        return {
            title: isActivating ? "Activate Employee" : "Deactivate Employee",
            description: isActivating
                ? "Are you sure you want to activate this employee? They will regain access to the system."
                : "Are you sure you want to deactivate this employee? They will no longer have access to the system.",
            confirmlabel: isActivating ? "Activate" : "Deactivate",
            type: isActivating ? "success" : "warning",
        };
    };

    const modalConfig = getModalConfig();

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={User}
                title="Employee"
                subtitle="Manage Employee List and there details"
                buttonEnable={!!addPermission && viewMode === "list"}
                buttonName="Add Employee"
                totalRecords={viewMode === "list" ? totalRecords : undefined}
                setOpenDialog={handleAddEmployee}
            />

            {viewMode === "occupancy" ? (
                <OccupancyView
                    viewSwitch={
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => switchView("list")}
                                className="gap-1.5"
                            >
                                <List className="h-4 w-4" />
                                List
                            </Button>
                            <Button
                                size="sm"
                                variant="brand"
                                onClick={() => switchView("occupancy")}
                                className="gap-1.5"
                            >
                                <Building className="h-4 w-4" />
                                Occupancy
                            </Button>
                        </div>
                    }
                />
            ) : (
                <>
                    <div className="px-3 pb-2 flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="brand"
                            onClick={() => switchView("list")}
                            className="gap-1.5"
                        >
                            <List className="h-4 w-4" />
                            List
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => switchView("occupancy")}
                            className="gap-1.5"
                        >
                            <Building className="h-4 w-4" />
                            Occupancy
                        </Button>
                    </div>
                    <Reactable
                        data={employeeData}
                        setData={setEmployeeData}
                        columns={employeeColumns}
                        serverSide
                        url={EMPLOYEE_PERSONAL_INFO}
                        setTotalCount={setTotalRecords}
                        serverTrigger={serverTrigger}
                        rowSize={editPermission ? "sm" : null}
                        isCSVRequired
                        isPDFRequired
                    />
                </>
            )}

            <ConfirmPopupModal
                open={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                    setSelectedRow(null);
                    setActionType("");
                }}
                onConfirm={handleConfirmToggle}
                title={modalConfig.title}
                description={modalConfig.description}
                type={modalConfig.type}
                confirmlabel={modalConfig.confirmlabel}
                destructive={actionType === "deactivate"}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default EmployeeList;
